import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Autocomplete, Box, Button, Chip, Collapse, IconButton, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField,
  Tooltip, Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import { fmt, listarReporte, mensajeError, opcionesReporte, simbolo } from "../services/ordenCompraService";

const headSx = { fontWeight: 800, whiteSpace: "nowrap", fontSize: 11.5, color: "text.secondary", textTransform: "uppercase", letterSpacing: 0.3 };
const cellSx = { py: 0.75, px: 1.25, fontSize: 12.5, verticalAlign: "top" };
const hoy = () => new Date().toISOString().slice(0, 10);
const primeroDeAnio = () => `${new Date().getFullYear()}-01-01`;

// Una orden del reporte: fila resumen y, al expandir, sus líneas con precio, total y costo unitario real.
function FilaOrden({ o, abierta, onAlternar, onVer }) {
  const sim = simbolo(o.moneda);
  return (
    <>
      <TableRow hover sx={{ "& > td": { borderBottom: abierta ? 0 : undefined } }}>
        <TableCell sx={{ ...cellSx, px: 0.5 }}>
          <IconButton size="small" aria-label={abierta ? "Ocultar detalle" : "Ver detalle"} onClick={onAlternar}>{abierta ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}</IconButton>
        </TableCell>
        <TableCell sx={{ ...cellSx, fontWeight: 800 }}>{o.id}</TableCell>
        <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>{o.fecha}</TableCell>
        <TableCell sx={{ ...cellSx, minWidth: 180 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>{o.proveedor}</Typography>
          {o.cliente && <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>Para: {o.cliente}</Typography>}
        </TableCell>
        <TableCell sx={cellSx}>{o.lineas.length ? `${o.lineas.length} línea${o.lineas.length === 1 ? "" : "s"}` : "Sin detalle"}</TableCell>
        <TableCell sx={cellSx}>{o.embarcado}{o.no_guia ? <Typography component="div" sx={{ fontSize: 11.5, color: "text.secondary" }}>Guía {o.no_guia}</Typography> : null}</TableCell>
        <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }} align="right">{o.costo_embarque ? `${sim} ${o.costo_embarque}` : "—"}</TableCell>
        <TableCell sx={cellSx}>
          {o.aduanas.length ? o.aduanas.map((a, i) => (
            <Box key={i} sx={{ whiteSpace: "nowrap" }}>{a.fecha_poliza || "—"} · F. {a.num_factura || "—"} · {simbolo(a.moneda)} {fmt(a.monto)}</Box>
          )) : "—"}
        </TableCell>
        <TableCell sx={{ ...cellSx, fontWeight: 800, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }} align="right">{sim} {fmt(o.total_compra)}</TableCell>
        <TableCell sx={cellSx}><Chip size="small" label={o.estado === "Proceso" ? "En proceso" : "Finalizada"} color={o.estado === "Proceso" ? "success" : "default"} sx={{ fontWeight: 700 }} /></TableCell>
        <TableCell sx={cellSx} align="center"><Tooltip title="Abrir la orden"><IconButton size="small" aria-label={`Abrir orden ${o.id}`} onClick={onVer}><VisibilityIcon fontSize="small" /></IconButton></Tooltip></TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={11} sx={{ p: 0, borderBottom: abierta ? undefined : 0 }}>
          <Collapse in={abierta} unmountOnExit>
            <Box sx={{ px: 2, py: 1, bgcolor: "action.hover" }}>
              {o.lineas.length === 0 ? <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>Esta orden no tiene detalle de compra.</Typography> : (
                <Table size="small" sx={{ bgcolor: "background.paper" }}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={headSx}>Cant.</TableCell>
                      <TableCell sx={headSx}>Descripción</TableCell>
                      <TableCell sx={headSx} align="right">Precio unitario</TableCell>
                      <TableCell sx={headSx} align="right">Total</TableCell>
                      <TableCell sx={headSx} align="right">Costo unitario real</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {o.lineas.map((l, i) => (
                      <TableRow key={i}>
                        <TableCell sx={cellSx}>{l.cantidad}</TableCell>
                        <TableCell sx={cellSx}>{l.descripcion}</TableCell>
                        <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }} align="right">{fmt(l.precio)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums", fontWeight: 700 }} align="right">{fmt(l.total)}</TableCell>
                        <TableCell sx={{ ...cellSx, fontVariantNumeric: "tabular-nums" }} align="right">{l.costo_unitario === null ? "—" : fmt(l.costo_unitario)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

// Reporte de órdenes de compra: por rango de fechas, proveedor o modelo y estado.
export default function ReporteOrdenesCompra() {
  const navigate = useNavigate();
  const [opciones, setOpciones] = useState({ proveedores: [], modelos: [] });
  const [filtros, setFiltros] = useState({ desde: primeroDeAnio(), hasta: hoy(), q: "", estado: "" });
  const [aplicados, setAplicados] = useState(null); // filtros con los que se consultó por última vez
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");
  const [pagina, setPagina] = useState(0);
  const [limite, setLimite] = useState(25);
  const [abiertas, setAbiertas] = useState({});

  useEffect(() => {
    opcionesReporte().then(({ data }) => setOpciones(data)).catch(() => setOpciones({ proveedores: [], modelos: [] }));
  }, []);

  const consultar = useCallback(async (f, pag, lim) => {
    setCargando(true);
    setError("");
    try {
      const { data } = await listarReporte({ desde: f.desde || undefined, hasta: f.hasta || undefined, q: f.q || undefined, estado: f.estado || undefined, page: pag + 1, limit: lim });
      setDatos(data);
    } catch (e) {
      setError(mensajeError(e, "No se pudo generar el reporte."));
    } finally {
      setCargando(false);
    }
  }, []);

  const buscar = (e) => {
    e.preventDefault();
    if (filtros.desde && filtros.hasta && filtros.desde > filtros.hasta) return setError("La fecha inicial no puede ser mayor que la final.");
    setAplicados(filtros);
    setPagina(0);
    setAbiertas({});
    consultar(filtros, 0, limite);
  };

  // Primera carga con los filtros por defecto (este año, todas las órdenes).
  // Solo al abrir la pantalla: la primera consulta usa los filtros iniciales
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setAplicados(filtros); consultar(filtros, 0, limite); }, []);

  const cambiarPagina = (p) => { setPagina(p); setAbiertas({}); consultar(aplicados, p, limite); };
  const cambiarFilas = (n) => { setLimite(n); setPagina(0); setAbiertas({}); consultar(aplicados, 0, n); };
  const set = (campo) => (e) => setFiltros((f) => ({ ...f, [campo]: e.target.value }));
  const todasAbiertas = datos?.rows?.length > 0 && datos.rows.every((o) => abiertas[o.id]);
  const alternarTodas = () => setAbiertas(todasAbiertas ? {} : Object.fromEntries((datos?.rows || []).map((o) => [o.id, true])));

  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Paper variant="outlined" component="form" onSubmit={buscar} sx={{ p: 1.5, display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "flex-start" }}>
        <TextField size="small" type="date" label="Fecha inicial" value={filtros.desde} onChange={set("desde")} slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 160 }} />
        <TextField size="small" type="date" label="Fecha final" value={filtros.hasta} onChange={set("hasta")} slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 160 }} />
        <Autocomplete
          freeSolo size="small" options={[...opciones.proveedores, ...opciones.modelos]} value={filtros.q} inputValue={filtros.q}
          onInputChange={(_, v) => setFiltros((f) => ({ ...f, q: v }))} filterOptions={(opts, { inputValue }) => opts.filter((o) => o.toLowerCase().includes(inputValue.toLowerCase())).slice(0, 40)}
          sx={{ width: { xs: "100%", sm: 340 } }}
          renderInput={(p) => <TextField {...p} label="Proveedor o modelo" placeholder="Escribe un proveedor o un modelo" />}
        />
        <TextField size="small" select label="Estado" value={filtros.estado} onChange={set("estado")} sx={{ width: 150 }}>
          <MenuItem value="">Todas</MenuItem>
          <MenuItem value="Proceso">En proceso</MenuItem>
          <MenuItem value="Finalizado">Finalizadas</MenuItem>
        </TextField>
        <Button type="submit" variant="contained" startIcon={<SearchIcon />} disabled={cargando} sx={{ textTransform: "none", fontWeight: 700 }}>Buscar</Button>
      </Paper>

      {error && <Alert severity="error" onClose={() => setError("")}>{error}</Alert>}

      {datos && (
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip label={`${datos.total} ${datos.total === 1 ? "orden" : "órdenes"}`} color="primary" sx={{ fontWeight: 700 }} />
            {datos.totales.dolares.ordenes > 0 && <Chip variant="outlined" label={`En dólares: ${datos.totales.dolares.ordenes} · $us ${fmt(datos.totales.dolares.monto)}`} />}
            {datos.totales.bolivianos.ordenes > 0 && <Chip variant="outlined" label={`En bolivianos: ${datos.totales.bolivianos.ordenes} · Bs ${fmt(datos.totales.bolivianos.monto)}`} />}
          </Box>
          {datos.rows.length > 0 && <Button size="small" onClick={alternarTodas} sx={{ textTransform: "none" }}>{todasAbiertas ? "Ocultar detalles" : "Ver todos los detalles"}</Button>}
        </Box>
      )}

      <Paper variant="outlined">
        <TableContainer sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "action.hover" }}>
                <TableCell padding="checkbox" />
                {["N.º", "Fecha", "Proveedor / Cliente", "Detalle", "Embarcador"].map((h) => <TableCell key={h} sx={headSx}>{h}</TableCell>)}
                <TableCell sx={headSx} align="right">Embarque</TableCell>
                <TableCell sx={headSx}>Aduana (póliza · factura · monto)</TableCell>
                <TableCell sx={headSx} align="right">Total compra</TableCell>
                <TableCell sx={headSx}>Estado</TableCell>
                <TableCell sx={headSx} align="center">Ver</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!datos?.rows?.length ? (
                <TableRow><TableCell colSpan={11} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>{cargando ? "Cargando…" : "No hay datos con esos filtros."}</TableCell></TableRow>
              ) : datos.rows.map((o) => (
                <FilaOrden key={o.id} o={o} abierta={!!abiertas[o.id]} onAlternar={() => setAbiertas((a) => ({ ...a, [o.id]: !a[o.id] }))} onVer={() => navigate(`/ordenes-compra/${o.id}`)} />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div" count={datos?.total || 0} page={pagina} rowsPerPage={limite} rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, p) => cambiarPagina(p)} onRowsPerPageChange={(e) => cambiarFilas(Number(e.target.value))}
          labelRowsPerPage="Filas" labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        />
      </Paper>
    </Box>
  );
}
